import { ArrowPathIcon } from '@heroicons/react/20/solid';

import { fieldSource } from '@/features/decisions/config/fieldLabels';
import type { InventoryCoveragePolicy } from '@/features/decisions/decisions.api.types';
import { fmtUnits } from '@/features/decisions/utils/historicalPerformanceFormat';

interface Props {
  // The selected policy (chosen via the Policy Details dropdown).
  policy: InventoryCoveragePolicy | undefined;
  isLoading: boolean;
  error: Error | null;
  // Reorder what-if: the recommended quantity becomes an editable input + slider.
  // Ephemeral (null = recommended).
  overrideReorder?: number | null;
  onOverrideChange?: (v: number | null) => void;
  reorderEditable?: boolean;
}

const fmt = (n: number) => Math.round(n).toLocaleString();

// Reorder quantity for the selected policy — recommended value shown once, made
// editable (input + slider) when the what-if is enabled. Policy selection lives
// in the Policy Details dropdown; this just reflects/tunes the chosen policy.
export function ReorderEditor({
  policy,
  isLoading,
  error,
  overrideReorder = null,
  onOverrideChange,
  reorderEditable = false,
}: Props) {
  const recommended = policy?.model_order_constrained ?? 0;
  const effective = overrideReorder ?? recommended;
  const max = Math.max(Math.ceil((recommended * 2) / 500) * 500, 500);
  const overridden = effective !== recommended;
  const delta = Math.round(effective - recommended);
  const commit = (n: number) => {
    const clamped = Math.min(max, Math.max(0, Math.round(n)));
    onOverrideChange?.(clamped === recommended ? null : clamped);
  };

  if (isLoading) return <div className="py-4 text-sm text-gray-600">Loading reorder…</div>;
  if (error) return <div className="py-4 text-sm text-gray-400">No coverage data for this decision period.</div>;
  if (!policy) return null;

  return (
    <div>
      <div
        className="text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        title={fieldSource('model_order_constrained')}
      >
        Recommended reorder
      </div>
      <div className="mt-1 flex items-start gap-3">
        <div className="flex items-baseline gap-2">
          {reorderEditable ? (
            <input
              type="number"
              value={Math.round(effective)}
              min={0}
              max={max}
              onChange={(e) => commit(Number(e.target.value))}
              aria-label="Reorder quantity"
              className="w-32 border-b border-[#274483]/50 bg-transparent text-2xl font-bold tabular-nums text-gray-900 focus:border-[#274483] focus:outline-none"
            />
          ) : (
            <span className="text-2xl font-bold tabular-nums text-gray-900">{fmtUnits(recommended)}</span>
          )}
          <span className="text-xs text-gray-400">Sale UOM</span>
        </div>
        {policy.model_beginning_available != null && (
          <div
            className="rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5"
            title={fieldSource('model_beginning_available')}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Current Inventory Position
            </div>
            <div className="text-sm font-bold tabular-nums text-gray-900">
              {fmtUnits(policy.model_beginning_available)}{' '}
              <span className="text-xs font-normal text-gray-400">Sale UOM</span>
            </div>
          </div>
        )}
      </div>
      {reorderEditable && (
        <div className="mt-2 pr-16">
          <input
            type="range"
            min={0}
            max={max}
            step={1}
            value={Math.min(max, Math.round(effective))}
            onChange={(e) => commit(Number(e.target.value))}
            className="w-full accent-[#274483]"
            aria-label="Reorder quantity slider"
          />
          <p className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-gray-400">
            <span>Recommended {fmt(recommended)} Sale UOM</span>
            {overridden && (
              <>
                <span className="font-medium text-amber-700">
                  {delta > 0 ? '+' : ''}
                  {fmt(delta)} vs recommended
                </span>
                <button
                  type="button"
                  onClick={() => onOverrideChange?.(null)}
                  className="flex items-center gap-1 font-medium text-[#274483] hover:text-[#1e3366]"
                >
                  <ArrowPathIcon className="h-3 w-3" /> Reset
                </button>
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
